import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  useTheme,
  alpha,
  LinearProgress,
  Fade,
  Avatar,
  AvatarGroup,
  Tooltip,
  Chip,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Assignment as SubtaskIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import useTaskStore from '../../../stores/taskStore';
import useConfirmDialog from '../../../hooks/useConfirmationDialog';

/**
 * Enhanced TaskSubtaskList component with MinimalTaskDetails design patterns
 */
const TaskSubtaskList = ({ task, canEdit, isSubmitting }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [ConfirmDialog, confirm] = useConfirmDialog();
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [hoveredSubtask, setHoveredSubtask] = useState(null);
  const inputRef = useRef(null);
  
  const {
    getTaskSubtasks,
    createSubtask,
    toggleSubtaskStatus,
    removeSubtask,
    taskSubtasks
  } = useTaskStore();
  
  // Get subtasks for this task
  const subtasks = taskSubtasks[task?._id] || [];
  
  // Calculate progress
  const completedCount = subtasks.filter(s => s.status === 'done').length;
  const totalCount = subtasks.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  
  // Load subtasks when component mounts
  useEffect(() => {
    if (task?._id) {
      getTaskSubtasks(task._id);
    }
  }, [task?._id, getTaskSubtasks]);
  
  // Focus input when adding
  useEffect(() => {
    if (isAddingSubtask && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddingSubtask]);
  
  // Handle add subtask
  const handleAddSubtask = async () => {
    if (!newSubtaskTitle?.trim() || !task) return;
    
    try {
      await createSubtask(task._id, {
        title: newSubtaskTitle.trim(),
        status: 'to_do',
        priority: task.priority || 'medium',
        projectId: task.projectId?._id || task.projectId
      });
      
      setNewSubtaskTitle('');
      setIsAddingSubtask(false);
      
      // Refresh subtasks
      await getTaskSubtasks(task._id);
    } catch (error) {
      console.log('Failed to add subtask:', error);
    }
  };
  
  // Handle key press in input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddSubtask();
    } else if (e.key === 'Escape') {
      setIsAddingSubtask(false);
      setNewSubtaskTitle('');
    }
  };
  
  // Get priority color
  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'urgent': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'low': return theme.palette.text.secondary;
      default: return theme.palette.text.secondary;
    }
  };
  
  // Toggle subtask completion
  const handleToggleSubtask = async (subtaskId) => {
    const subtask = subtasks.find(s => s._id === subtaskId);
    if (!subtask || !task) return;
    
    try {
      // Use the store method for toggling subtask status
      await toggleSubtaskStatus(task._id, subtaskId);
      
      // Refresh subtasks to ensure we have the latest data
      await getTaskSubtasks(task._id);
    } catch (error) {
      console.log('Failed to toggle subtask:', error);
    }
  };
  
  // Delete subtask handler
  const handleDeleteSubtask = async (subtaskId) => {
    if (!task || !subtaskId) return;
    
    // Confirm deletion
    const confirmed = await confirm({
      title: 'Delete Subtask',
      content: 'Are you sure you want to delete this subtask? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (!confirmed) return;
    
    try {
      await removeSubtask(task._id, subtaskId);
      
      // Refresh subtasks
      await getTaskSubtasks(task._id);
    } catch (error) {
      console.log('Failed to delete subtask:', error);
    }
  };
  
  return (
    <Box sx={{
      p: 1.5,
      bgcolor: alpha(theme.palette.background.paper, 0.5),
      borderRadius: 1,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    }}>
      {/* Compact progress indicator */}
      {subtasks.length > 0 && (
        <Box sx={{ 
          mb: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Progress
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600,
                color: progress === 100 ? theme.palette.success.main : theme.palette.text.secondary,
              }}
            >
              {completedCount}/{totalCount}
              {progress === 100 && (
                <CheckIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: theme.palette.success.main }} />
              )}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.divider, 0.08),
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                bgcolor: progress === 100 
                  ? theme.palette.success.main
                  : theme.palette.primary.main,
              }
            }}
          />
        </Box>
      )}
      
      {/* Subtasks List */}
      {subtasks.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          {subtasks.map((subtask, index) => (
            <Fade in key={subtask._id} timeout={300 * (index + 1)}>
              <Box
                onMouseEnter={() => setHoveredSubtask(subtask._id)}
                onMouseLeave={() => setHoveredSubtask(null)}
                onClick={() => {
                  // Open the subtask details in a drawer
                  const { openTaskDrawer } = useTaskStore.getState();
                  openTaskDrawer(subtask._id);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  py: 0.75,
                  mb: 0.25,
                  bgcolor: subtask.status === 'done' 
                    ? alpha(theme.palette.action.hover, 0.02)
                    : theme.palette.background.paper,
                  borderRadius: 0.5,
                  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.08),
                    borderColor: alpha(theme.palette.divider, 0.15),
                  },
                  ...(subtask.status === 'done' && {
                    opacity: 0.6,
                    '&:hover': {
                      opacity: 0.8,
                    }
                  })
                }}
              >
                {/* Checkbox */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleSubtask(subtask._id);
                  }}
                  disabled={isSubmitting || !canEdit}
                  sx={{
                    p: 0.5,
                    mr: 1.5,
                    color: subtask.status === 'done' 
                      ? theme.palette.success.main 
                      : theme.palette.action.active,
                    '&:hover': {
                      bgcolor: alpha(
                        subtask.status === 'done' 
                          ? theme.palette.success.main 
                          : theme.palette.primary.main,
                        0.08
                      )
                    }
                  }}
                >
                  {subtask.status === 'done' ? (
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <UncheckedIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
                
                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: subtask.status === 'done' ? 400 : 500,
                      textDecoration: subtask.status === 'done' ? 'line-through' : 'none',
                      color: subtask.status === 'done' 
                        ? theme.palette.text.disabled 
                        : theme.palette.text.primary,
                      opacity: subtask.status === 'done' ? 0.6 : 1,
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {subtask.title}
                  </Typography>
                  
                  {/* Meta Info */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    flexWrap: 'wrap'
                  }}>
                    {/* Priority Indicator */}
                    {subtask.priority && subtask.priority !== 'medium' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FlagIcon 
                          sx={{ 
                            fontSize: 14, 
                            color: getPriorityColor(subtask.priority) 
                          }} 
                        />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: getPriorityColor(subtask.priority),
                            fontWeight: 500
                          }}
                        >
                          {subtask.priority}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Assignee */}
                    {subtask.assignedTo && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Avatar
                          src={subtask.assignedTo.photo}
                          sx={{
                            width: 18,
                            height: 18,
                            fontSize: 10,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          {subtask.assignedTo.firstName?.[0]}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          {subtask.assignedTo.firstName} {subtask.assignedTo.lastName}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Due Date */}
                    {subtask.dueDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(subtask.dueDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Collaborators */}
                    {subtask.collaborators && subtask.collaborators.length > 0 && (
                      <AvatarGroup 
                        max={3}
                        sx={{
                          '& .MuiAvatar-root': {
                            width: 18,
                            height: 18,
                            fontSize: 10,
                            borderWidth: 1,
                          }
                        }}
                      >
                        {subtask.collaborators.map((collab) => (
                          <Avatar
                            key={collab.user._id}
                            src={collab.user.photo}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                            }}
                          >
                            {collab.user.firstName?.[0]}
                          </Avatar>
                        ))}
                      </AvatarGroup>
                    )}
                  </Box>
                </Box>
                
                {/* Delete Action */}
                {canEdit && (
                  <Fade in={hoveredSubtask === subtask._id}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSubtask(subtask._id);
                      }}
                      sx={{
                        ml: 1,
                        p: 0.5,
                        color: theme.palette.error.main,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.15),
                        }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Fade>
                )}
              </Box>
            </Fade>
          ))}
        </Box>
      ) : null}
      
      {/* Add Subtask Input */}
      {isAddingSubtask ? (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5, 
          mt: subtasks.length > 0 ? 0.5 : 0,
          p: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 0.5,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}>
          <TextField
            ref={inputRef}
            size="small"
            fullWidth
            value={newSubtaskTitle}
            onChange={(e) => setNewSubtaskTitle(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Add subtask title..."
            disabled={isSubmitting}
            sx={{
              margin: 0, // Fix the margin-bottom issue
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.875rem',
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiOutlinedInput-input': {
                py: 0.5,
                px: 1,
                fontSize: '0.875rem',
              }
            }}
          />
          <IconButton size="small" onClick={handleAddSubtask} disabled={!newSubtaskTitle?.trim() || isSubmitting}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => { setIsAddingSubtask(false); setNewSubtaskTitle(''); }} disabled={isSubmitting}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        /* Add Button */
        canEdit && (
          <Box
            onClick={() => setIsAddingSubtask(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 0.75,
              mt: subtasks.length > 0 ? 0.5 : 0,
              borderRadius: 0.5,
              cursor: 'pointer',
              border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
              color: theme.palette.text.secondary,
              transition: 'all 0.15s ease',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                color: theme.palette.primary.main,
              },
              ...((!canEdit || isSubmitting) && {
                opacity: 0.5,
                cursor: 'not-allowed',
                pointerEvents: 'none'
              })
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              Add subtask
            </Typography>
          </Box>
        )
      )}
      
      <ConfirmDialog />
    </Box>
  );
};

export default TaskSubtaskList;